import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

/** Duration of open/close animation */
const animationDuration = 350;

/**
 * Challenge expansion panel component
 */
@Component({
  selector: 'app-challenge-expansion-panel',
  templateUrl: './challenge-expansion-panel.component.html',
  styleUrls: ['./challenge-expansion-panel.component.scss'],
  animations: [
    trigger('expansionCollapseState', [
      state(
        'hidden',
        style({
          height: '0',
        }),
      ),
      state(
        'shown',
        style({
          height: '*',
        }),
      ),
      transition('hidden <=> shown', animate(animationDuration + 'ms ease-in-out')),
    ]),
  ],
})
export class ChallengeExpansionPanelComponent implements OnChanges {
  /** Getter and setter of whether panel is expanded */
  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(opened: boolean) {
    if (this._opened !== opened) {
      this._opened = opened;
      this.openedChange.emit(this._opened);
    }
  }
  /** Internal opened state */
  private _opened: boolean = false;

  /** Emits if opened state has changed */
  @Output()
  public openedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /** Whether the extension can be triggered only by pressing the button or everywhere. */
  @Input()
  public trigger: 'button' | 'all' = 'button';

  /** Whether panel can be collapsed */
  @Input()
  public isCollapsible: boolean = true;

  /** Whether a border should be shown */
  @Input()
  public borderColor?: 'error' = null;

  /** Reference to HTML Element */
  public nativeElement: HTMLElement;

  /** Whether content is visible (content is removed completely from DOM if false) */
  public displayContent: boolean = false;
  /** Timeout for displayContent variable to hide content after animation is played */
  private displayContentTimeout: number;

  // ----- REFERENCES ----- //

  /** Reference to object in HTML code */
  constructor(private elementRef: ElementRef) {
    this.nativeElement = elementRef.nativeElement;
  }

  /**
   * Updates the visibility of content if opened input is changed
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.opened) {
      this.updateContentVisibility();
    }
  }

  /**
   * Toggles expansion state
   */
  public toggleOpenState(isButton: boolean): void {
    if (!this.isCollapsible) {
      return;
    }

    // Do nothing if anything is clicked but the arrow button and only button should trigger
    if (!isButton && this.trigger === 'button') {
      return;
    }
    this.opened = !this.opened;

    this.updateContentVisibility();
  }

  /**
   * Updates the visibility of the content.
   * Adds a timeout when the extension panel is no longer open to remove the content only after the animation has been fully played
   */
  private updateContentVisibility(): void {
    // If panel is not collapsible always display the content
    if (!this.isCollapsible) {
      this.displayContent = true;
      return;
    }

    clearTimeout(this.displayContentTimeout);

    if (this.opened) {
      this.displayContent = true;
    } else {
      this.displayContentTimeout = window.setTimeout(() => {
        this.displayContent = false;
      }, animationDuration);
    }
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/theme/components/challenge-expansion-panel/challenge-expansion-panel.component.ts